/**
 * Shared MSAL token acquisition helper.
 *
 * Escalation strategy:
 *   1. acquireTokenSilent (from cache / refresh-token)
 *   2. acquireTokenSilent with forceRefresh (bypasses cache)
 *   3. acquireTokenPopup (interactive — last resort)
 *
 * This prevents the "Missing or invalid Authorization header" error that
 * occurs when a user returns with an expired Azure AD session and the
 * silent path fails without escalation.
 */
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, msalReady, apiScopes } from '../config/auth';

export async function acquireToken(forceRefresh = false): Promise<string | null> {
  await msalReady;
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;

  const account = msalInstance.getActiveAccount() || accounts[0];
  try {
    const response = await msalInstance.acquireTokenSilent({
      ...apiScopes,
      account,
      forceRefresh,
    });
    return response.accessToken;
  } catch (err) {
    if (forceRefresh || err instanceof InteractionRequiredAuthError) {
      try {
        const response = await msalInstance.acquireTokenPopup(apiScopes);
        return response.accessToken;
      } catch {
        return null;
      }
    }
    // First silent attempt failed for a non-interaction reason —
    // retry once with forceRefresh before giving up.
    if (!forceRefresh) {
      return acquireToken(true);
    }
    return null;
  }
}
